import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ModelDetailView.module.scss";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  View,
  ViewHeader,
} from "@/components";
import { DotsVerticalIcon } from "@vetty/icons";
import {
  formatSpeciesDisplayName,
  getCatalogStatusBadgeMeta,
} from "../../_constants/content-models-ui.const";
import { UiFlash } from "../ui-flash";
import { CreateCatalogModelDrawer } from "../create-catalog-model-drawer/CreateCatalogModelDrawer.comp";
import { ModelDetailToolbar } from "../model-detail-toolbar";
import {
  modelDetailVersionMatchesFilter,
  type ModelDetailVersionsFilter,
} from "../../_constants/model-detail-toolbar.const";
import Link from '@/components/AppLink';
import { useRouter } from '@/lib/router-navigation';
import axios from "axios";
import { axiosInstance, getApiErrorCode } from "@/helpers/axios";
import { ForceCatalogApproveDrawer } from "../force-catalog-approve-drawer/ForceCatalogApproveDrawer.comp";
import { SubsceneManagerDrawer } from "../subscene-manager-drawer/SubsceneManagerDrawer.comp";
import {
  DeleteModelVersionModal,
  type DeleteModelVersionPreview,
} from "../delete-model-version-modal";

type ModelVersionRowData = {
  id: string;
  version_label: string;
  version_seq?: number;
  status: string;
  updated_at: number;
  change_description: string | null;
  species_slug: string;
  species_display_name: string;
  approval_count: number;
  created_by_account_id: string | null;
  force_self_approve_by?: string | null;
};

function buildDeletePreview(
  version: ModelVersionRowData,
  versions: ModelVersionRowData[],
  activeVersionId: string | null
): DeleteModelVersionPreview {
  const isLive =
    version.id === activeVersionId || version.status === "active";
  if (!isLive) {
    return { kind: "version_only", versionLabel: version.version_label };
  }
  const previous = [...versions]
    .filter((v) => v.id !== version.id && v.status === "superseded")
    .sort((a, b) => {
      const seqDiff = (b.version_seq ?? 0) - (a.version_seq ?? 0);
      if (seqDiff !== 0) return seqDiff;
      return (b.updated_at ?? 0) - (a.updated_at ?? 0);
    })[0];
  if (previous) {
    return {
      kind: "rollback",
      versionLabel: version.version_label,
      restoreToLabel: previous.version_label,
    };
  }
  return { kind: "remove_species", versionLabel: version.version_label };
}

type ModelDetailViewProps = {
  speciesSlug: string;
};

function apiMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const m = err.response?.data as { message?: string } | undefined;
    if (m?.message && typeof m.message === "string") return m.message;
  }
  return "Something went wrong.";
}

type ModelDetailVersionRowProps = {
  v: ModelVersionRowData;
  requiredApprovals: number;
  busyKey: string | null;
  previewOpeningId: string | null;
  setBusyKey: (k: string | null) => void;
  run: (action: () => Promise<void>) => Promise<void>;
  openPreview: (versionId: string) => Promise<void>;
  onApprove: (v: ModelVersionRowData) => void | Promise<void>;
  onManageSubscenes?: (v: ModelVersionRowData) => void;
  onDelete?: (v: ModelVersionRowData) => void;
  rowClassName?: string;
};

function ModelDetailVersionRow({
  v,
  requiredApprovals,
  busyKey,
  previewOpeningId,
  setBusyKey,
  run,
  openPreview,
  onApprove,
  onManageSubscenes,
  onDelete,
  rowClassName,
}: ModelDetailVersionRowProps) {
  const meta = getCatalogStatusBadgeMeta(v.status);
  const ac = typeof v.approval_count === "number" ? v.approval_count : 0;
  const hasForceSelf = Boolean(v.force_self_approve_by);
  const canPublish =
    v.status === "in_review" &&
    (ac >= requiredApprovals || hasForceSelf);
  const actionsBusy = busyKey !== null || previewOpeningId === v.id;

  return (
    <div className={rowClassName ?? styles["comp__row"]}>
      <div className={styles["comp__cell"]}>
        <strong>{v.version_label}</strong>
        {v.change_description ? (
          <>
            <br />
            {v.change_description}
          </>
        ) : null}
      </div>
      <div>
        <span
          className={classNames(
            styles["comp__badge"],
            styles[`comp__badge--${meta.variant}`]
          )}
        >
          {meta.label}
        </span>
      </div>
      <div className={styles["comp__cell"]}>
        {v.status === "in_review"
          ? `${ac}/${requiredApprovals}${hasForceSelf ? " · forced" : ""}`
          : "—"}
      </div>
      <div className={styles["comp__cell"]}>
        {new Date(v.updated_at).toLocaleString()}
      </div>
      <div className={styles["comp__cell"]}>
        {v.created_by_account_id
          ? `${v.created_by_account_id.slice(0, 8)}…`
          : "—"}
      </div>
      <div className={styles["comp__actions"]}>
        <Dropdown>
          <DropdownTrigger className={styles["comp__dropdownTrigger"]}>
            <button
              type="button"
              className={styles["comp__menuTrigger"]}
              aria-label={`Actions for ${v.version_label}`}
              aria-haspopup="menu"
              disabled={actionsBusy}
            >
              <DotsVerticalIcon
                style={{ width: 20, height: 20 }}
                aria-hidden
              />
            </button>
          </DropdownTrigger>
          <DropdownContent width={200} lockBodyScroll={false}>
            <DropdownItem
              id={`${v.id}-preview`}
              selectable={false}
              className={styles["comp__menuItem"]}
              disabled={previewOpeningId === v.id}
              onClick={() => void openPreview(v.id)}
            >
              {previewOpeningId === v.id ? "Opening…" : "Open preview"}
            </DropdownItem>
            {v.status === "draft" ? (
              <DropdownItem
                id={`${v.id}-submit`}
                selectable={false}
                className={styles["comp__menuItem"]}
                disabled={busyKey !== null}
                onClick={() => {
                  setBusyKey(`submit-${v.id}`);
                  void run(async () => {
                    await axiosInstance.post(
                      `/v2/models/versions/${encodeURIComponent(v.id)}/submit-review`
                    );
                  }).finally(() => setBusyKey(null));
                }}
              >
                Submit to review
              </DropdownItem>
            ) : null}
            {v.status === "in_review" ? (
              <>
                <DropdownItem
                  id={`${v.id}-approve`}
                  selectable={false}
                  className={styles["comp__menuItem"]}
                  disabled={busyKey !== null}
                  onClick={() => void onApprove(v)}
                >
                  Approve
                </DropdownItem>
                <DropdownItem
                  id={`${v.id}-publish`}
                  selectable={false}
                  className={styles["comp__menuItem"]}
                  disabled={!canPublish || busyKey !== null}
                  onClick={() => {
                    setBusyKey(`act-${v.id}`);
                    void run(async () => {
                      await axiosInstance.post(
                        `/v2/models/versions/${encodeURIComponent(v.id)}/activate`
                      );
                    }).finally(() => setBusyKey(null));
                  }}
                >
                  Publish
                </DropdownItem>
              </>
            ) : null}
            {v.status === "superseded" ? (
              <DropdownItem
                id={`${v.id}-restore`}
                selectable={false}
                className={styles["comp__menuItem"]}
                disabled={busyKey !== null}
                onClick={() => {
                  setBusyKey(`sa-${v.id}`);
                  void run(async () => {
                    await axiosInstance.post(
                      `/v2/models/versions/${encodeURIComponent(v.id)}/set-active`
                    );
                  }).finally(() => setBusyKey(null));
                }}
              >
                Restore
              </DropdownItem>
            ) : null}
            {v.status !== "processing" ? (
              <DropdownItem
                id={`${v.id}-subscenes`}
                selectable={false}
                className={styles["comp__menuItem"]}
                onClick={() => onManageSubscenes?.(v)}
              >
                Subscenes
              </DropdownItem>
            ) : null}
            {v.status !== "processing" ? (
              <DropdownItem
                id={`${v.id}-delete`}
                selectable={false}
                className={classNames(
                  styles["comp__menuItem"],
                  styles["comp__menuItem--danger"]
                )}
                disabled={busyKey !== null}
                onClick={() => onDelete?.(v)}
              >
                Delete
              </DropdownItem>
            ) : null}
          </DropdownContent>
        </Dropdown>
      </div>
    </div>
  );
}

export const ModelDetailView = ({ speciesSlug }: ModelDetailViewProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [liveCatalogLabel, setLiveCatalogLabel] = useState<string | null>(null);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [versions, setVersions] = useState<ModelVersionRowData[]>([]);
  const [requiredApprovals, setRequiredApprovals] = useState(1);
  const [flash, setFlash] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [previewOpeningId, setPreviewOpeningId] = useState<string | null>(null);
  const [versionsFilter, setVersionsFilter] =
    useState<ModelDetailVersionsFilter>("all");
  const [addVersionDrawerOpen, setAddVersionDrawerOpen] = useState(false);
  const [forceDrawer, setForceDrawer] = useState<{
    id: string;
    titleLine: string;
  } | null>(null);
  const [subsceneManager, setSubsceneManager] = useState<{
    versionId: string;
    versionLabel: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ModelVersionRowData | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!speciesSlug) return;
    setError(null);
    setLoading(true);
    try {
      const [catRes, verRes] = await Promise.all([
        axiosInstance.get("/v2/models/catalog"),
        axiosInstance.get("/v2/models/versions", { params: { speciesSlug } }),
      ]);

      const items = catRes.data?.data?.items as
        | Array<{
            slug: string;
            displayName: string;
            versionLabel: string | null;
            activeVersionId: string | null;
          }>
        | undefined;
      const row = items?.find((r) => r.slug === speciesSlug);

      const vData = verRes.data?.data;
      const list = (vData?.items ?? []) as ModelVersionRowData[];
      const sorted = [...list].sort(
        (a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0)
      );

      const name =
        row?.displayName ??
        sorted[0]?.species_display_name ??
        speciesSlug;
      setDisplayName(formatSpeciesDisplayName(name));
      setLiveCatalogLabel(row?.versionLabel ?? null);
      setActiveVersionId(row?.activeVersionId ?? null);

      setRequiredApprovals(
        typeof vData?.requiredApprovals === "number" ? vData.requiredApprovals : 1
      );
      setVersions(sorted);
    } catch {
      setError("Could not load model.");
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [speciesSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  const liveVersion = useMemo(() => {
    if (!activeVersionId) return null;
    return versions.find((v) => v.id === activeVersionId) ?? null;
  }, [activeVersionId, versions]);

  const otherVersions = useMemo(() => {
    if (!liveVersion) return versions;
    return versions.filter((v) => v.id !== liveVersion.id);
  }, [versions, liveVersion]);

  const filteredOtherVersions = useMemo(() => {
    return otherVersions.filter((v) =>
      modelDetailVersionMatchesFilter(v.status, versionsFilter)
    );
  }, [otherVersions, versionsFilter]);

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
      setFlash(null);
      await load();
    } catch (e) {
      setFlash(apiMessage(e));
    }
  };

  const openPreview = async (versionId: string) => {
    setPreviewOpeningId(versionId);
    try {
      const { data } = await axiosInstance.get(
        `/v2/models/versions/${encodeURIComponent(versionId)}/viewer-token`
      );
      const url = data?.data?.viewerUrl as string | undefined;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPreviewOpeningId(null);
    }
  };

  const handleApprove = async (v: ModelVersionRowData) => {
    setBusyKey(`ap-${v.id}`);
    setFlash(null);
    try {
      await axiosInstance.post(
        `/v2/models/versions/${encodeURIComponent(v.id)}/approve`
      );
      await load();
    } catch (e) {
      if (getApiErrorCode(e) === "cannot_self_approve") {
        setForceDrawer({
          id: v.id,
          titleLine: `${formatSpeciesDisplayName(v.species_display_name)} · ${v.version_label}`,
        });
      } else {
        setFlash(apiMessage(e));
      }
    } finally {
      setBusyKey(null);
    }
  };

  const deletePreview = useMemo(() => {
    if (!deleteTarget) return null;
    return buildDeletePreview(deleteTarget, versions, activeVersionId);
  }, [deleteTarget, versions, activeVersionId]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setBusyKey(`del-${deleteTarget.id}`);
    setFlash(null);
    try {
      const { data } = await axiosInstance.delete(
        `/v2/models/versions/${encodeURIComponent(deleteTarget.id)}`
      );
      const outcome = data?.data?.outcome as string | undefined;
      setDeleteTarget(null);
      if (outcome === "species_removed") {
        router.push("/models");
        return;
      }
      if (outcome === "rolled_back" && data?.data?.restoredVersionLabel) {
        setFlash(
          `Version deleted. Catalog rolled back to ${data.data.restoredVersionLabel}.`
        );
      }
      await load();
    } catch (e) {
      setFlash(apiMessage(e));
    } finally {
      setIsDeleting(false);
      setBusyKey(null);
    }
  };

  if (!speciesSlug) {
    return (
      <View>
        <div className={styles["comp__not-found"]}>
          <p>Invalid model.</p>
          <Link href="/models">Return to library</Link>
        </div>
      </View>
    );
  }

  if (loading) {
    return (
      <View>
        <div className={styles["comp__not-found"]}>Loading…</div>
      </View>
    );
  }

  if (error) {
    return (
      <View>
        <div className={styles["comp__not-found"]}>
          <p>{error}</p>
          <Link href="/models">Return to library</Link>
        </div>
      </View>
    );
  }

  const versionRowProps = {
    requiredApprovals,
    busyKey,
    previewOpeningId,
    setBusyKey,
    run,
    openPreview,
    onApprove: handleApprove,
    onManageSubscenes: (v: ModelVersionRowData) =>
      setSubsceneManager({ versionId: v.id, versionLabel: v.version_label }),
    onDelete: (v: ModelVersionRowData) => setDeleteTarget(v),
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading={displayName}
          description={`Catalog slug <code>${speciesSlug}</code>. Approvals required to publish: <strong>${requiredApprovals}</strong>. ${
            liveCatalogLabel
              ? `Live catalog label: <strong>${liveCatalogLabel}</strong>.`
              : "No version published to the catalog yet."
          }`}
        />

        {liveVersion ? (
          <div className={styles["comp__liveDeploy"]}>
            <div className={styles["comp__liveDeployHeader"]}>
              <span className={styles["comp__livePill"]}>Live</span>
              <span className={styles["comp__liveDeploySubtitle"]}>
                {liveVersion.version_label} · serving the catalog
              </span>
            </div>
            <div className={styles["comp__liveDeployBody"]}>
              <ModelDetailVersionRow
                v={liveVersion}
                rowClassName={classNames(
                  styles["comp__row"],
                  styles["comp__row--inLiveCard"]
                )}
                {...versionRowProps}
              />
            </div>
          </div>
        ) : null}

        <ModelDetailToolbar
          filter={versionsFilter}
          onFilterChange={setVersionsFilter}
          onAddVersionClick={() => setAddVersionDrawerOpen(true)}
        />

        {otherVersions.length > 0 ? (
          <div className={styles["comp__versions"]}>
            <div className={styles["comp__versions-header"]}>
              <div className={styles["comp__vh"]}>Version</div>
              <div className={styles["comp__vh"]}>Status</div>
              <div className={styles["comp__vh"]}>Approvals</div>
              <div className={styles["comp__vh"]}>Updated</div>
              <div className={styles["comp__vh"]}>Author</div>
              <div className={styles["comp__vh"]}>Actions</div>
            </div>
            {filteredOtherVersions.length === 0 ? (
              <div className={styles["comp__row"]}>
                <div
                  className={styles["comp__cell"]}
                  style={{ gridColumn: "1 / -1" }}
                >
                  No versions match this filter.
                </div>
              </div>
            ) : (
              filteredOtherVersions.map((v) => (
                <ModelDetailVersionRow key={v.id} v={v} {...versionRowProps} />
              ))
            )}
          </div>
        ) : (
          <p className={styles["comp__empty"]}>
            {versions.length === 0
              ? "No versions for this species yet."
              : "No other versions — only the live deployment is listed above."}
          </p>
        )}

        <CreateCatalogModelDrawer
          isOpen={addVersionDrawerOpen}
          onClose={() => setAddVersionDrawerOpen(false)}
          onCreated={() => void load()}
          lockedSpeciesSlug={speciesSlug}
          lockedSpeciesDisplayName={displayName}
        />

        <ForceCatalogApproveDrawer
          isOpen={forceDrawer !== null}
          onClose={() => setForceDrawer(null)}
          versionId={forceDrawer?.id ?? null}
          titleLine={forceDrawer?.titleLine ?? ""}
          onSuccess={() => void load()}
        />

        <SubsceneManagerDrawer
          isOpen={subsceneManager !== null}
          onClose={() => setSubsceneManager(null)}
          onChanged={() => load()}
          parentVersionId={subsceneManager?.versionId ?? ""}
          parentVersionLabel={subsceneManager?.versionLabel ?? ""}
        />

        <DeleteModelVersionModal
          isOpen={deleteTarget !== null}
          preview={deletePreview}
          speciesDisplayName={displayName}
          onClose={() => {
            if (!isDeleting) setDeleteTarget(null);
          }}
          onConfirm={() => void handleConfirmDelete()}
          isDeleting={isDeleting}
        />

        <UiFlash message={flash} onDismiss={() => setFlash(null)} />
      </div>
    </View>
  );
};
